import PropTypes from 'prop-types';
import { useState } from 'react';
// @mui
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
// components
import { Button, Divider, Menu, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { useBoolean } from 'src/hooks/use-boolean';
import Label from 'src/components/label';
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

export default function QuickStaffTableRow({
    row,
    onViewRow,
    onEditRow,
    onDeleteRow,
}) {
    const { first_name, last_name, display_name, profile_img, phone_no, email, role, enabled_features } = row;

    const { isUserRoleHasAccess } = useAuthContext();

    const popover = usePopover();

    const confirm = useBoolean();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <TableRow hover >
                <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar alt={display_name} src={profile_img} sx={{ mr: 2 }}>
                        {display_name.charAt(0).toUpperCase()} 
                    </Avatar>
                    {`${first_name} ${last_name}`}
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Label color='info'>
                        {role === 'TOUR_CONSULTANT' ? 'CONSULTANT': role}
                    </Label>
                </TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>{email}</TableCell>

                <TableCell sx={{ whiteSpace: 'nowrap' }}>{phone_no ?? 'N/A'}</TableCell>

                <TableCell align='center' sx={{ whiteSpace: 'nowrap' }}>
                    {
                        enabled_features?.length > 0
                        ?
                        <IconButton onClick={handleClick}>
                            <Iconify icon="material-symbols:security" width={24} />
                        </IconButton>
                        :
                        'N/A'
                    }
                </TableCell>

                <TableCell align="right" sx={{ px: 1 }}>
                    <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
                        <Iconify icon="eva:more-vertical-fill" />
                    </IconButton>
                </TableCell>
            </TableRow>

            <CustomPopover
                open={popover.open}
                onClose={popover.onClose}
                arrow="right-top"
                sx={{ width: 160 }}
            >

                <MenuItem
                    onClick={() => {
                        onViewRow();
                        popover.onClose();
                    }}
                >
                    <Iconify icon="solar:eye-bold" />
                    View
                </MenuItem>

                {isUserRoleHasAccess(8, 5) && (
                    <MenuItem
                        onClick={() => {
                            onEditRow();
                            popover.onClose();
                        }}
                    >
                        <Iconify icon="solar:pen-bold" />
                        Edit
                    </MenuItem>
                )}

                {isUserRoleHasAccess(8, 5) && <Divider sx={{ borderStyle: 'dashed' }} />}

                {isUserRoleHasAccess(8, 5) && (
                    <MenuItem
                        onClick={() => {
                            confirm.onTrue();
                            popover.onClose();
                        }}
                        sx={{ color: 'error.main' }}
                    >
                        <Iconify icon="solar:trash-bin-trash-bold" />
                        Remove
                    </MenuItem>
                )}
            </CustomPopover>

            <ConfirmDialog
                open={confirm.value}
                onClose={confirm.onFalse}
                title="Remove"
                content={`Are you sure want to remove ${display_name} ?`}
                action={
                    <Button variant="contained" color="error" onClick={() => {
                        onDeleteRow();
                        confirm.onFalse();
                    }}>
                        Remove
                    </Button>
                }
            />

            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                list: {
                    'aria-labelledby': 'basic-button',
                },
                }}
            >
                <Button
                    onClick={handleClose}
                    disableRipple
                    sx={{
                        backgroundColor: 'transparent',
                        '&: hover': {
                            backgroundColor: 'transparent'
                        }
                    }}
                >
                    <Stack
                        direction='column'
                        justifyContent='start'
                        alignItems='start'
                        p={2}
                        sx={{
                            minWidth: 100
                        }}
                        spacing={2}
                    >
                        {
                            enabled_features?.map((item, index) =>
                                <Typography
                                    key={index}
                                    fontSize={16}
                                    textAlign='left'
                                >
                                    <Iconify icon="material-symbols:circle" width={10} sx={{mr: 1}}/>
                                    {item}
                                </Typography>
                            )
                        }
                    </Stack>
                </Button>
            </Menu>
        </>
    );
}

QuickStaffTableRow.propTypes = {
    onDeleteRow: PropTypes.func,
    onViewRow: PropTypes.func,
    onEditRow: PropTypes.func,
    row: PropTypes.object,
};
